/**
 * AI Sourcing from Candidates Tab
 * Requires job selection before launching sourcing
 */
import React, { useState, useEffect } from 'react';
import { Sparkles, AlertCircle, Briefcase, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import AISourcingPanel from './AISourcingPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Candidate Match Card Component
const CandidateMatchCard = ({ candidate, jobId, onAddToPipeline }) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Determine tier based on match score
  const getTierInfo = (score) => {
    if (score >= 90) return { name: 'Platinum', color: 'bg-purple-100 text-purple-800', icon: '💎' };
    if (score >= 75) return { name: 'Gold', color: 'bg-yellow-100 text-yellow-800', icon: '⭐' };
    if (score >= 60) return { name: 'Silver', color: 'bg-gray-100 text-gray-800', icon: '🥈' };
    return { name: 'Bronze', color: 'bg-orange-100 text-orange-800', icon: '🥉' };
  };

  const matchScore = candidate.match_score || candidate.overall_score || 0;
  const tier = getTierInfo(matchScore);
  const isInternal = candidate.source === 'internal' || candidate.source_type === 'internal';

  const handleAddToPipeline = async () => {
    setIsAdding(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: jobId,
          candidate_id: candidate.id,
          source: 'ai_sourcing',
          status: 'new'
        })
      });

      if (!response.ok) throw new Error('Failed to add candidate');
      
      if (onAddToPipeline) onAddToPipeline();
    } catch (error) {
      console.error('Error adding to pipeline:', error);
      toast.error('Failed to add candidate to pipeline');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
      <div className="flex items-start justify-between">
        {/* Left: Candidate Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-lg">{candidate.name || candidate.full_name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${tier.color}`}>
              {tier.icon} {tier.name}
            </span>
            {isInternal && (
              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                📁 Internal
              </span>
            )}
            {!isInternal && (
              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                🌐 External
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
            <div>📧 {candidate.email || 'N/A'}</div>
            <div>📍 {candidate.location || candidate.city || 'N/A'}</div>
            <div>💼 {candidate.current_job_title || candidate.current_title || 'N/A'}</div>
            <div>🏢 {candidate.current_company || 'N/A'}</div>
          </div>

          {/* Skills Match */}
          {candidate.skills && candidate.skills.length > 0 && (
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-700">Skills: </span>
              <div className="flex flex-wrap gap-1 mt-1">
                {candidate.skills.slice(0, 5).map((skill, idx) => (
                  <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                    {skill}
                  </span>
                ))}
                {candidate.skills.length > 5 && (
                  <span className="text-xs px-2 py-0.5 text-gray-500">
                    +{candidate.skills.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: Match Score & Action */}
        <div className="flex flex-col items-end gap-3 ml-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{matchScore}%</div>
            <div className="text-xs text-gray-500">Match Score</div>
          </div>

          <button
            onClick={handleAddToPipeline}
            disabled={isAdding}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
          >
            {isAdding ? 'Adding...' : 'Add to Pipeline'}
          </button>
        </div>
      </div>

      {/* Match Breakdown (if available) */}
      {candidate.match_breakdown && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-2 text-xs">
            {candidate.match_breakdown.skills_match !== undefined && (
              <div>
                <span className="text-gray-500">Skills:</span>
                <span className="ml-1 font-medium">{candidate.match_breakdown.skills_match}%</span>
              </div>
            )}
            {candidate.match_breakdown.experience_match !== undefined && (
              <div>
                <span className="text-gray-500">Experience:</span>
                <span className="ml-1 font-medium">{candidate.match_breakdown.experience_match}%</span>
              </div>
            )}
            {candidate.match_breakdown.location_match !== undefined && (
              <div>
                <span className="text-gray-500">Location:</span>
                <span className="ml-1 font-medium">{candidate.match_breakdown.location_match}%</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AISourcingFromCandidatesTab = ({ selectedUser, myTeam, teamLead, onClose, onComplete, onRefresh }) => {
  const [validJobs, setValidJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSourcingPanel, setShowSourcingPanel] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [sourcingRunId, setSourcingRunId] = useState(null);
  const [sourcedCandidates, setSourcedCandidates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  // Calculate allowed clients based on user role and team
  // CRITICAL: Regular recruiters INHERIT clients from their team lead
  // See: /app/CRITICAL_RULES.md - Rule #3
  const getAllowedClients = () => {
    if (!selectedUser) return [];

    const isLeadRecruiter = selectedUser.role === 'lead_recruiter' || selectedUser.role === 'lead';
    const isRegularRecruiter = selectedUser.role === 'recruiter';
    
    if (isLeadRecruiter) {
      // Lead Recruiter: Use their own assigned_clients (which covers their whole team)
      const userClients = selectedUser.assigned_clients || [];
      console.log(`[AI Sourcing Modal] Lead Recruiter ${selectedUser.full_name}: ${userClients.length} allowed clients`);
      return userClients;
    } else if (isRegularRecruiter) {
      // Regular Recruiter: Inherit from team lead's assigned_clients
      if (teamLead) {
        const inheritedClients = teamLead.assigned_clients || [];
        console.log(`[AI Sourcing Modal] Regular Recruiter ${selectedUser.full_name}: Inherited ${inheritedClients.length} clients from team lead ${teamLead.full_name}`);
        return inheritedClients;
      } else {
        // Fallback: Use own assigned_clients if no team found
        const userClients = selectedUser.assigned_clients || [];
        console.warn(`[AI Sourcing Modal] Regular Recruiter ${selectedUser.full_name}: No team found, using own ${userClients.length} clients`);
        return userClients;
      }
    } else {
      // Other roles: Use own assigned_clients
      const userClients = selectedUser.assigned_clients || [];
      console.log(`[AI Sourcing Modal] User ${selectedUser.full_name} (${selectedUser.role}): ${userClients.length} allowed clients`);
      return userClients;
    }
  };

  const allowedClients = getAllowedClients();

  useEffect(() => {
    loadValidJobs();
  }, []);

  // Check if a job belongs to "My Jobs" (same logic as JobsTableView)
  const isMyJob = (job) => {
    if (!selectedUser?.id || !job) return false;
    
    // Check if user created this job
    if (job.created_by === selectedUser.id) {
      return true;
    }
    
    // Primary ownership: job's primary recruiter
    if (job.primary_recruiter_id === selectedUser.id || job.recruiter_id === selectedUser.id) {
      return true;
    }
    
    // Check assigned recruiters array
    if (job.assigned_recruiters && Array.isArray(job.assigned_recruiters)) {
      if (job.assigned_recruiters.some(ar => ar.recruiter_id === selectedUser.id)) {
        return true;
      }
    }
    
    // CRITICAL: Check if job is assigned to ANY team member (not just current user)
    // This includes jobs assigned to other recruiters in the same team
    if (myTeam && myTeam.length > 0) {
      const teamMemberIds = myTeam.map(member => member.id);
      
      // Check if job's primary recruiter is any team member
      if (job.primary_recruiter_id && teamMemberIds.includes(job.primary_recruiter_id)) {
        return true;
      }
      
      if (job.recruiter_id && teamMemberIds.includes(job.recruiter_id)) {
        return true;
      }
      
      // Check if any assigned recruiter is a team member
      if (job.assigned_recruiters && Array.isArray(job.assigned_recruiters)) {
        if (job.assigned_recruiters.some(ar => teamMemberIds.includes(ar.recruiter_id))) {
          return true;
        }
      }
    }
    
    // Team inheritance: Check if job's company is in team lead's assigned_clients
    if (teamLead && teamLead.assigned_clients) {
      const jobCompany = job.client || job.client_company || '';
      const isTeamClient = teamLead.assigned_clients.some(assignedClient => {
        const clientWords = assignedClient.split(/\s+/);
        const firstWord = clientWords[0];
        return jobCompany.startsWith(firstWord) || jobCompany === assignedClient;
      });
      if (isTeamClient) {
        return true;
      }
    }
    
    return false;
  };

  // Fetch sourced candidates with match scores
  const fetchSourcedCandidates = async (runId) => {
    setLoadingResults(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-sourcing/runs/${runId}/candidates`);
      if (!response.ok) throw new Error('Failed to fetch sourced candidates');
      
      const data = await response.json();
      console.log(`[AI Sourcing Modal] Fetched ${data.candidates?.length || 0} sourced candidates`);
      
      setSourcedCandidates(data.candidates || []);
      
      // Fetch sourcing run details to get suggestions
      const runResponse = await fetch(`${BACKEND_URL}/api/ai-sourcing/runs/${runId}`);
      if (runResponse.ok) {
        const runData = await runResponse.json();
        setSuggestions(runData.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching sourced candidates:', error);
      toast.error('Failed to load sourced candidates');
    } finally {
      setLoadingResults(false);
    }
  };

  // Load sourced candidates when showResults becomes true
  React.useEffect(() => {
    if (showResults && sourcingRunId) {
      fetchSourcedCandidates(sourcingRunId);
    }
  }, [showResults, sourcingRunId]);

  const loadValidJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-sourcing/valid-jobs`);
      if (!response.ok) throw new Error('Failed to load jobs');
      
      const data = await response.json();
      
      // CRITICAL: Filter to show ONLY "My Jobs" (jobs user or their team is responsible for)
      const myJobs = data.jobs.filter(job => isMyJob(job));
      
      // Smart filtering: If a parent job has sub-jobs, show ONLY the sub-jobs, not the parent
      const parentJobIds = new Set();
      const subJobs = [];
      
      // First pass: identify all parent jobs that have sub-jobs
      myJobs.forEach(job => {
        if (job.parent_job_id) {
          parentJobIds.add(job.parent_job_id);
          subJobs.push(job);
        }
      });
      
      // Second pass: filter out parent jobs that have sub-jobs
      const smartFilteredJobs = myJobs.filter(job => {
        // If this is a parent job with sub-jobs, exclude it
        if (parentJobIds.has(job.id)) {
          return false;
        }
        // Include all other jobs (sub-jobs and standalone jobs)
        return true;
      });
      
      console.log(`[AI Sourcing Modal] My Jobs: ${myJobs.length} total (${smartFilteredJobs.length} shown - ${parentJobIds.size} parent jobs hidden)`);
      
      setValidJobs(smartFilteredJobs);
    } catch (error) {
      console.error('Error loading valid jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobSelect = () => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }
    setShowSourcingPanel(true);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-center text-gray-600">Loading available jobs...</p>
        </div>
      </div>
    );
  }

  if (validJobs.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-center mb-2">No Active Jobs Found</h2>
          <p className="text-gray-600 text-center mb-4">
            AI Sourcing is available for your active jobs (open and accepting candidates).
            You currently have no active jobs assigned to you or your team.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (showSourcingPanel && selectedJob) {
    return (
      <AISourcingPanel
        job={selectedJob}
        onClose={() => {
          setShowSourcingPanel(false);
          if (onClose) onClose();
        }}
        onCandidatesFound={(runId, jobId, resultsData) => {
          // Show results in the modal itself (no navigation)
          setShowSourcingPanel(false);
          setSourcingRunId(runId);
          setShowResults(true);
          
          // Call parent callback if provided
          if (onComplete) {
            onComplete(jobId, runId);
          }
        }}
      />
    );
  }

  // Show results view after sourcing completes
  if (showResults && selectedJob) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">AI Sourcing Results</h2>
                <p className="text-purple-100 mt-1">
                  {selectedJob.title} - Found {sourcedCandidates.length} matching candidates
                </p>
              </div>
              <button
                onClick={() => {
                  setShowResults(false);
                  if (onClose) onClose();
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Results Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loadingResults ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mb-4"></div>
                <p className="text-gray-600">Loading sourced candidates...</p>
              </div>
            ) : sourcedCandidates.length === 0 ? (
              <div className="max-w-3xl mx-auto">
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-orange-100 p-6 rounded-full mb-6">
                    <AlertCircle className="w-12 h-12 text-orange-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">No Matching Candidates Found</h3>
                  <p className="text-gray-600 text-center mb-8">
                    We searched all selected sources but couldn't find candidates matching your criteria.<br/>
                    Here are some suggestions to improve your results:
                  </p>
                </div>

                {/* Smart Suggestions */}
                {suggestions && suggestions.length > 0 && (
                  <div className="space-y-4 mb-8">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">💡 Smart Suggestions</h4>
                    {suggestions.map((suggestion, index) => (
                      <div 
                        key={index}
                        className={`bg-white border-2 rounded-lg p-5 transition-all hover:shadow-md ${
                          suggestion.priority === 'high' ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="text-3xl">{suggestion.icon}</div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-800 mb-1">{suggestion.title}</h5>
                            <p className="text-gray-600 text-sm mb-2">{suggestion.description}</p>
                            <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm text-blue-800">
                              <strong>Action:</strong> {suggestion.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => setShowResults(false)}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
                  >
                    Adjust Criteria & Search Again
                  </button>
                  <button
                    onClick={() => {
                      setShowResults(false);
                      if (onClose) onClose();
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {sourcedCandidates.map((candidate, index) => (
                  <CandidateMatchCard
                    key={candidate.id || index}
                    candidate={candidate}
                    jobId={selectedJob.id}
                    onAddToPipeline={() => {
                      toast.success(`${candidate.name} added to pipeline!`);
                      // Refresh parent data if callback provided
                      if (onRefresh) onRefresh();
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-gray-200 p-6 bg-gray-50 flex items-center justify-between">
            <button
              onClick={() => {
                setShowResults(false);
                setSelectedJob(null);
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              Back to Job Selection
            </button>
            <button
              onClick={() => {
                setShowResults(false);
                if (onClose) onClose();
              }}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-6 h-6" />
            <h2 className="text-2xl font-bold">AI Source Candidates</h2>
          </div>
          <p className="text-purple-100">
            Showing your active jobs ({validJobs.length} available) - Select a job to source candidates
          </p>
        </div>

        {/* Job Selection */}
        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Select Job <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <select
                value={selectedJob?.id || ''}
                onChange={(e) => {
                  const job = validJobs.find(j => j.id === e.target.value);
                  setSelectedJob(job);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 appearance-none bg-white"
              >
                <option value="">-- Select a job --</option>
                {validJobs.map((job) => {
                  // Format display: show parent indicator for sub-jobs
                  const isSubJob = job.parent_job_id;
                  const displayText = isSubJob 
                    ? `└─ ${job.title} [${job.display_id}]`
                    : `${job.title} [${job.display_id}]`;
                  
                  return (
                    <option key={job.id} value={job.id}>
                      {displayText} - {job.location || 'Remote'} ({job.submission_count} submissions)
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Selected Job Info */}
          {selectedJob && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                Selected Job Details
                {selectedJob.parent_job_id && (
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                    Sub-position
                  </span>
                )}
              </h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Job ID:</span> {selectedJob.display_id}</p>
                <p><span className="font-medium">Title:</span> {selectedJob.title}</p>
                
                {/* CRITICAL: Client Company - MUST be shown */}
                <p>
                  <span className="font-medium">Client Company:</span>{' '}
                  <span className={!selectedJob.client_company ? 'text-red-600 font-bold' : ''}>
                    {selectedJob.client_company || '⚠️ MISSING - CRITICAL ERROR'}
                  </span>
                </p>
                
                <p><span className="font-medium">Location:</span> {selectedJob.location || 'Not specified'}</p>
                <p><span className="font-medium">Status:</span> <span className="capitalize">{selectedJob.status}</span></p>
                <p><span className="font-medium">Days Open:</span> {selectedJob.days_open} days</p>
                <p><span className="font-medium">Current Submissions:</span> {selectedJob.submission_count}</p>
                
                {/* CRITICAL: Required Skills - MUST be shown for AI sourcing */}
                {selectedJob.skills_required && selectedJob.skills_required.length > 0 ? (
                  <div className="mt-3 pt-3 border-t border-purple-200">
                    <p className="font-medium text-purple-900 mb-2">🎯 Required Skills:</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedJob.skills_required.map((skill, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-red-200">
                    <p className="text-red-600 font-bold text-xs">
                      ⚠️ WARNING: No skills defined for this job! AI sourcing may not find relevant candidates.
                    </p>
                  </div>
                )}
                
                {selectedJob.parent_job_id && (
                  <p className="text-purple-700 mt-2 italic text-xs">
                    ℹ️ This is a sub-position. Sourcing candidates for this specific role.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Helper Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              <span className="font-medium">How it works:</span> Once you select a job,
              you&apos;ll be able to configure AI sourcing preferences and find matching candidates
              from internal database and external sources.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleJobSelect}
              disabled={!selectedJob}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              <Search className="w-5 h-5" />
              Continue to AI Sourcing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISourcingFromCandidatesTab;
